import { type MissingPerson } from "@/hooks/useWhosMissing";
import { type ReportAggregation } from "@glorify-llc/accounting-core";
import { buildReportSummaryItem } from "@/lib/metrics/metric-registry";

export type WhosMissingReportContext = {
  asOf: Date;
  missingPeople: MissingPerson[];
};

/**
 * Coerce a possibly-null/undefined/NaN/string-numeric count to a non-negative
 * finite integer. Without coercion, a single non-finite upstream `daysMissing`
 * or `attendanceCount` would propagate NaN through the sort comparator
 * (NaN-NaN === NaN, all comparisons false) and silently scramble row order.
 * String upstream (`"42"`) would also subtract correctly but leak into the
 * report payload as the string `"42"`.
 */
function toFiniteNonNegativeInt(value: unknown): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return 0;
  // Counts and day-counts cannot be negative or fractional.
  return Math.max(0, Math.trunc(n));
}

/**
 * Normalize a `Date | string | null | undefined` `lastAttendance` to an ISO
 * string (or null). The hook constructs `lastAttendance` as `new Date(...)`,
 * which produces an `Invalid Date` if the upstream `check_in_time` is
 * unparseable — and `format(invalidDate, ...)` from date-fns then THROWS,
 * crashing the whole `WhosMissingReport` render. Snapshot serialisation also
 * round-trips Date→ISO→string, so the adapter must accept either.
 */
function lastAttendanceToIso(value: unknown): string | null {
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isFinite(t) ? value.toISOString() : null;
  }
  if (typeof value === "string" && value.length > 0) {
    const t = new Date(value).getTime();
    return Number.isFinite(t) ? new Date(t).toISOString() : null;
  }
  return null;
}

/**
 * Render a person's display label with a "Unknown" fallback so an empty
 * `firstName`/`lastName` upstream (legacy seeds, partial imports) doesn't
 * surface as a blank row in the table or CSV export.
 */
function formatPersonLabel(firstName: unknown, lastName: unknown): string {
  const first = typeof firstName === "string" ? firstName.trim() : "";
  const last = typeof lastName === "string" ? lastName.trim() : "";
  const combined = `${first} ${last}`.replace(/\s+/g, " ").trim();
  return combined.length > 0 ? combined : "Unknown";
}

/** Trim and null-out blank/whitespace-only contact strings. */
function cleanContact(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function buildWhosMissingReportAggregation({
  asOf,
  missingPeople,
}: WhosMissingReportContext): ReportAggregation {
  // Defensive: callers (snapshot serializers, loading-state shells) sometimes
  // pass `undefined` when the upstream query is still loading. The previous
  // `missingPeople.map(...)` would throw and crash the report container.
  const safe = Array.isArray(missingPeople) ? missingPeople : [];

  // Dedupe by id. The hook builds `personMap` keyed by `person_id`, so
  // duplicates are unlikely in practice — but a snapshot-replay path or
  // future caller could feed in a flat list with collisions, and React would
  // emit duplicate-key warnings + the "People" summary would over-count.
  const byId = new Map<string, {
    id: string;
    label: string;
    email: string | null;
    phone: string | null;
    lastAttendance: string | null;
    daysMissing: number;
    attendanceCount: number;
    _lastAttendanceMs: number;
  }>();

  for (const person of safe) {
    if (!person) continue;
    const rawId = typeof person.id === "string" ? person.id.trim() : "";
    if (rawId.length === 0) continue;
    const lastAttendanceIso = lastAttendanceToIso(person.lastAttendance);
    const lastAttendanceMs =
      lastAttendanceIso === null ? -Infinity : new Date(lastAttendanceIso).getTime();
    const row = {
      id: rawId,
      label: formatPersonLabel(person.firstName, person.lastName),
      email: cleanContact(person.email),
      phone: cleanContact(person.phone),
      lastAttendance: lastAttendanceIso,
      daysMissing: toFiniteNonNegativeInt(person.daysMissing),
      attendanceCount: toFiniteNonNegativeInt(person.attendanceCount),
      _lastAttendanceMs: lastAttendanceMs,
    };
    const existing = byId.get(rawId);
    if (existing) {
      // Take the higher daysMissing (the most-recently-attended duplicate
      // would have a lower daysMissing; we want to surface the worst case).
      // Sum attendanceCount so the visible total matches reality across
      // duplicates from different upstream paths.
      existing.daysMissing = Math.max(existing.daysMissing, row.daysMissing);
      existing.attendanceCount += row.attendanceCount;
      // Keep the most-recent lastAttendance — that is the one the user
      // expects to see ("when did we last see them at all").
      if (row._lastAttendanceMs > existing._lastAttendanceMs) {
        existing.lastAttendance = row.lastAttendance;
        existing._lastAttendanceMs = row._lastAttendanceMs;
      }
      // Prefer a non-Unknown label / non-null contact over the existing one.
      if (existing.label === "Unknown" && row.label !== "Unknown") {
        existing.label = row.label;
      }
      if (existing.email === null && row.email !== null) existing.email = row.email;
      if (existing.phone === null && row.phone !== null) existing.phone = row.phone;
      continue;
    }
    byId.set(rawId, row);
  }

  // Stable, locale-independent sort: most days-missing first (the natural
  // pastoral-care reading order — who needs follow-up most urgently), then
  // by label, then by id. We use `<`/`>` rather than `localeCompare` so the
  // order is identical across locales / Node-vs-browser ICU builds (#8 in
  // the recurring-bug catalog).
  const rows = Array.from(byId.values())
    .sort((a, b) => {
      if (a.daysMissing !== b.daysMissing) {
        return b.daysMissing - a.daysMissing;
      }
      if (a.label < b.label) return -1;
      if (a.label > b.label) return 1;
      if (a.id < b.id) return -1;
      if (a.id > b.id) return 1;
      return 0;
    })
    .map((p) => ({
      id: p.id,
      label: p.label,
      email: p.email,
      phone: p.phone,
      lastAttendance: p.lastAttendance,
      daysMissing: p.daysMissing,
      attendanceCount: p.attendanceCount,
      entityType: "person" as const,
      // Encode the id segment so any non-UUID/synthetic id (e.g. from a
      // snapshot replay) doesn't break the URL with spaces or `&`.
      drilldownUrl: `/people/${encodeURIComponent(p.id)}`,
    }));

  return {
    asOf,
    summary: [buildReportSummaryItem("report.whos_missing.people_count", rows.length)],
    rows,
  };
}
